/**
 * The row-density control — `LabsLtd/bata-ui#21`, ADR 0088 as amended (audit v5b D-08).
 *
 * **Where it lives now.** In the identity menu, beside the language: a preference is chosen
 * once, per planner, and a control above every grid read as a setting OF that grid.
 *
 * **The name, since Package C6 (2026-09-30).** The designer's brief part 1 §1 makes the density
 * the rows' height in `DataTable` as well as `PlanningGrid` (audit A8): «Compatta 32 · Normale 40
 * · Comoda 48». So the control is «Densità delle righe» and its options are «Compatta · Normale ·
 * Comoda», in that order, smallest first. A `DataTable` follows it when it passes `density` —
 * every table on a page converted to the S/E templates does; a hand-written `<table>` still does
 * not. What follows is the history of why it once sat on the grid.
 *
 * ## Why this component exists at all
 *
 * design/09 §6 gives three row heights — Comfortable 40, Default 32, Compact 24, re-stepped to
 * 48 / 40 / 32 by brief part 1 §1 — and
 * `densityRowHeight[mode]` is applied by exactly one component in the product,
 * `PlanningGrid`. The control that sets it used to sit in the app chrome, beside the locale
 * and theme controls, which are genuinely global. Measured on 2026-09-18: four routes
 * declare a `grid` surface and 45 hand-written `<table>`s on other routes ignore the setting
 * entirely. So the chrome promised estate-wide behaviour that one component delivers.
 *
 * The estate owner ruled option 1 on #21 on 2026-09-18: row height is `PlanningGrid`'s own
 * contract, and the control moves onto the grid so that it stops promising more than it
 * governs. This is that control.
 *
 * ## What is deliberately NOT here
 *
 * The **store** does not move. `useDensity()` stays a single persisted store in
 * `./density.ts`, so two grids on one screen agree, and the choice still survives a reload —
 * §6 calls the persistence out by name, and a preference that resets on login teaches a
 * planner that their preferences are not respected. Moving the control is a statement about
 * where the affordance belongs, not an argument for per-grid state.
 *
 * Nothing here decides whether the other 45 tables *should* share a density contract. #21's
 * ruling says in terms that this is a separate and larger question and must not be smuggled
 * in alongside it.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { FOCUS_RING } from '../primitives/focus';
import { DENSITY_MODES, type DensityMode } from '../tokens/generated/tokens';
import { useDensity } from './density';

/** The menu's order, smallest row first: «Compatta · Normale · Comoda» (brief part 1 §1). */
export const DENSITY_MENU_ORDER: readonly DensityMode[] = [...DENSITY_MODES].reverse();

export interface DensityControlProps {
  /**
   * Accessible name.
   *
   * Defaults to `density.menuLabel` — *"Row density"*: since brief part 1 §1 the density is the
   * rows' height in the data tables as well as the planning grids (see above).
   */
  readonly labelKey?: string;
}

export function DensityControl({ labelKey = 'density.menuLabel' }: DensityControlProps): React.JSX.Element {
  const { t } = useTranslation();
  const { mode, setMode } = useDensity();
  // `useId`, not a module constant: a screen may mount two grids, and two `<label for>`
  // pointing at one id makes the second control unreachable by its name.
  const controlId = useId();

  return (
    <div
      // Marks the control for `e2e/compact-density.spec.ts`'s guard, which asserts that the
      // identity menu carries it on a route that mounts a grid.
      data-planning-grid-density=""
      // Stacked like the language select above it in the same menu.
      className="flex flex-col gap-1"
    >
      <label className="text-label text-ink-secondary" htmlFor={controlId}>
        {t(labelKey)}
      </label>
      <select
        id={controlId}
        value={mode}
        onChange={(e) => setMode(e.target.value as DensityMode)}
        className={`bg-surface-raised border-hairline text-body text-ink-primary h-8 rounded-control border px-1 ${FOCUS_RING}`}
      >
        {DENSITY_MENU_ORDER.map((m) => (
          <option key={m} value={m}>{t(`density.${m}`)}</option>
        ))}
      </select>
    </div>
  );
}
